import { z } from "zod";
import { normalizeMemberId } from "@medbillai/utils/memberId";
import { FormIsoDateField, FormTextField, isRealIsoDate, MINIMUM_USER_AGE_RULE, meetsMinimumUserAge, useFormEngine } from "../../../lib/form-engine";
import type { Insurer, Name } from "./usePlanSetup";
const detailsSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  dateOfBirth: z.string(),
  insurer: z.custom<Insurer>().nullable(),
  memberId: z.string(),
  termsAccepted: z.boolean()
});
type DetailsValues = z.input<typeof detailsSchema>;
export type IdentityProps = {
  askName?: boolean;
  askInsurer?: boolean;
  /** The member ID flag; a payer that needs one still decides. */
  askMemberId?: boolean;
  needsTerms?: (given: {
    name: Name | null;
    dateOfBirth: string;
  }) => boolean;
  initialName?: Name | null;
  initialDob?: string | null;
  initialInsurer?: Insurer | null;
  onDone: (details: {
    name: Name | null;
    dateOfBirth: string;
    insurer: Insurer | null;
    memberId: string | undefined;
  }) => void;
};

/**
 * The details a plan check sends, on one form engine form. Every rule runs in
 * one refinement, so a submit flags every missing detail at once.
 */
export function useIdentityForm({
  askName = false,
  askInsurer = false,
  askMemberId = false,
  needsTerms = () => false,
  initialName = null,
  initialDob = null,
  initialInsurer = null,
  onDone
}: IdentityProps) {
  const named = (values: DetailsValues) => askName ? {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim()
  } : null;
  const termsAsked = (values: DetailsValues) => needsTerms({
    name: named(values),
    dateOfBirth: values.dateOfBirth
  });
  const memberIdAsked = (values: DetailsValues) => askMemberId && Boolean(values.insurer?.memberIdRequired);
  const schema = detailsSchema.superRefine((values, ctx) => {
    const flag = (path: keyof DetailsValues, message: string) => ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [path],
      message
    });
    if (askName && values.firstName.trim() === "") {
      flag("firstName", "Enter your first name");
    }
    if (askName && values.lastName.trim() === "") {
      flag("lastName", "Enter your last name");
    }
    if (values.dateOfBirth === "") {
      flag("dateOfBirth", "Enter your date of birth");
    } else if (!isRealIsoDate(values.dateOfBirth)) {
      flag("dateOfBirth", "Enter a valid date");
    } else if (!meetsMinimumUserAge(values.dateOfBirth)) {
      flag("dateOfBirth", MINIMUM_USER_AGE_RULE);
    }
    if (askInsurer && !values.insurer) {
      flag("insurer", "Choose your insurer");
    }
    if (memberIdAsked(values) && normalizeMemberId(values.memberId) === null) {
      flag("memberId", "Enter your member ID");
    }
    if (termsAsked(values) && !values.termsAccepted) {
      flag("termsAccepted", "Accept the terms");
    }
  });
  const {
    form,
    submit
  } = useFormEngine<DetailsValues, DetailsValues>({
    defaultValues: {
      firstName: initialName?.firstName ?? "",
      lastName: initialName?.lastName ?? "",
      dateOfBirth: initialDob ?? "",
      insurer: initialInsurer,
      memberId: "",
      termsAccepted: false
    },
    schema,
    onSubmit: () => ({
      status: "ok"
    }),
    onSubmitSuccess: values => onDone({
      name: named(values),
      dateOfBirth: values.dateOfBirth,
      insurer: values.insurer,
      memberId: memberIdAsked(values) ? normalizeMemberId(values.memberId) ?? undefined : undefined
    })
  });
  return {
    form,
    askName,
    termsAsked,
    memberIdAsked,
    submit: () => void submit()
  };
}
type IdentityForm = ReturnType<typeof useIdentityForm>;
export function IdentityFields({
  identity,
  focusDob = false
}: {
  identity: IdentityForm;
  /** Focus the date of birth on mount, e.g. after tapping Edit. */
  focusDob?: boolean;
}) {
  return <>
      {identity.askName ? <NameFields identity={identity} /> : null}
      <DobField identity={identity} autoFocus={focusDob} data-sentry-element="DobField" data-sentry-source-file="IdentityForm.tsx" />
    </>;
}
export function NameFields({
  identity
}: {
  identity: IdentityForm;
}) {
  const {
    form
  } = identity;
  return <>
      <form.Field name="firstName" data-sentry-element="form.Field" data-sentry-source-file="IdentityForm.tsx">
        {field => <FormTextField field={field} required label="First name" autoCapitalize="words" autoComplete="given-name" autoCorrect={false} spellCheck={false} />}
      </form.Field>
      <form.Field name="lastName" data-sentry-element="form.Field" data-sentry-source-file="IdentityForm.tsx">
        {field => <FormTextField field={field} required label="Last name" autoCapitalize="words" autoComplete="family-name" autoCorrect={false} spellCheck={false} />}
      </form.Field>
    </>;
}
export function DobField({
  identity,
  autoFocus = false
}: {
  identity: IdentityForm;
  autoFocus?: boolean;
}) {
  const {
    form
  } = identity;
  return <form.Field name="dateOfBirth" data-sentry-element="form.Field" data-sentry-component="DobField" data-sentry-source-file="IdentityForm.tsx">
      {field => <FormIsoDateField field={field} required label="Date of birth" autoFocus={autoFocus} />}
    </form.Field>;
}